import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PROJECT_TYPES, REFERRAL_SOURCES, CTA_LABEL } from "@/data/content";

const EMPTY = { name: "", phone: "", email: "", project_type: "", referral_source: "", message: "" };

export default function ConsultationDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.project_type) {
      toast.error("Please complete name, phone, email and project type.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Request received. We will reach out shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setForm(EMPTY); setDone(false); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="consultation-dialog"
        className="max-w-lg bg-navy border border-gold/30 text-ivory rounded-none p-0 max-h-[92vh] overflow-y-auto"
      >
        {done ? (
          <div className="p-10 text-center flex flex-col items-center gap-5" data-testid="consultation-success">
            <CheckCircle2 className="h-14 w-14 text-gold" strokeWidth={1} />
            <h3 className="font-serif text-3xl">Thank You</h3>
            <p className="text-[#A1B0C4] leading-relaxed">
              Your consultation request is in. A member of our team will reach out within a few hours
              to schedule your free 3D design session.
            </p>
          </div>
        ) : (
          <div className="p-8 sm:p-10">
            <DialogHeader className="text-left space-y-2 mb-6">
              <p className="text-gold text-xs tracking-luxe uppercase font-semibold">No cost. No obligation.</p>
              <DialogTitle className="font-serif text-3xl sm:text-4xl font-light text-ivory">
                {CTA_LABEL}
              </DialogTitle>
              <DialogDescription className="text-[#A1B0C4]">
                Tell us a little about your project and we will be in touch.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name" required>
                  <Input data-testid="lead-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                    className="rounded-none bg-navy-surface border-gold/25 text-ivory focus-visible:ring-gold" placeholder="Your name" />
                </Field>
                <Field label="Phone" required>
                  <Input data-testid="lead-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                    className="rounded-none bg-navy-surface border-gold/25 text-ivory focus-visible:ring-gold" placeholder="(318) 000-0000" />
                </Field>
              </div>
              <Field label="Email" required>
                <Input data-testid="lead-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                  className="rounded-none bg-navy-surface border-gold/25 text-ivory focus-visible:ring-gold" placeholder="you@email.com" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Project Type" required>
                  <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
                    <SelectTrigger data-testid="lead-project-type" className="rounded-none bg-navy-surface border-gold/25 text-ivory focus:ring-gold">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-navy border-gold/25 text-ivory">
                      {PROJECT_TYPES.map((t) => (
                        <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-ivory">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="How did you hear of us?">
                  <Select value={form.referral_source} onValueChange={(v) => set("referral_source", v)}>
                    <SelectTrigger data-testid="lead-referral" className="rounded-none bg-navy-surface border-gold/25 text-ivory focus:ring-gold">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-navy border-gold/25 text-ivory">
                      {REFERRAL_SOURCES.map((t) => (
                        <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-ivory">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Tell us about your project">
                <Textarea data-testid="lead-message" value={form.message} onChange={(e) => set("message", e.target.value)}
                  className="rounded-none bg-navy-surface border-gold/25 text-ivory focus-visible:ring-gold min-h-[90px]"
                  placeholder="Optional - what are you dreaming up?" />
              </Field>
              <button
                data-testid="lead-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-navy-deep font-semibold tracking-luxe uppercase text-sm px-8 py-4 rounded-none hover:bg-gold-light transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Sending" : "Request My Consultation"}
              </button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#A1B0C4] text-xs tracking-wider uppercase">
        {label} {required && <span className="text-gold">*</span>}
      </Label>
      {children}
    </div>
  );
}
